
/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  Language, Translations, Product, CaseStudy, Resource, Testimonial, Partner, 
  SEOSettings, BlogPost, FaqItem, Certificate, Badge, IndustryCategory 
} from '../types';
import { 
  TRANSLATIONS, PRODUCTS, CASES, RESOURCES, TESTIMONIALS, PARTNERS, 
  BLOG_POSTS, FAQ_ITEMS, CERTIFICATES 
} from '../constants';
import { 
  SiteData, fetchRemoteData, saveRemoteData, getStorageType, 
  setManualApiUrl, getBaseUrl 
} from '../services/apiStorage';
import { getNestedValue, setNestedValue, deepMerge } from '../utils/objectHelpers';
import { 
  generateTranslations, importProductFromUrl, 
  importArticleFromUrl, parseArticleFromText 
} from '../services/geminiService';

const CACHE_KEY = 'meitai_local_cache';

interface EditorContextType {
  isEditMode: boolean;
  toggleEditMode: () => void;
  saveChanges: () => Promise<void>;
  refreshData: () => Promise<void>;
  hasUnsavedChanges: boolean;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error' | 'translating';
  dataDescription: string;
  translations: Translations;
  products: Product[];
  cases: CaseStudy[];
  resources: Resource[];
  testimonials: Testimonial[];
  partners: Partner[];
  blogPosts: BlogPost[];
  faqItems: FaqItem[];
  certificates: Certificate[];
  badges: Badge[];
  seoSettings: SEOSettings;
  hiddenNavItems: string[];
  currentApiUrl: string;
  updateTranslation: (lang: Language, path: string, value: string) => void;
  updateProduct: (id: string, field: keyof Product | 'features' | 'specs', lang: Language, value: any) => void;
  updateProductImage: (id: string, url: string) => void;
  deleteProduct: (id: string) => void;
  addProduct: () => void;
  toggleProductFeatured: (id: string) => void;
  reorderProducts: (newProducts: Product[]) => void;
  updateCase: (id: string, field: keyof CaseStudy, lang: Language, value: any) => void;
  updateCaseImage: (id: string, url: string) => void;
  addCase: () => void;
  deleteCase: (id: string) => void;
  updateResource: (id: string, field: keyof Resource, lang: Language, value: any) => void;
  updateResourceFile: (id: string, url: string, size: string) => void;
  addResource: () => void;
  deleteResource: (id: string) => void;
  updateTestimonial: (id: string, field: keyof Testimonial, lang: Language, value: any) => void;
  addTestimonial: () => void;
  deleteTestimonial: (id: string) => void;
  reorderTestimonials: (newTestimonials: Testimonial[]) => void;
  updatePartner: (id: string, name: string, logoUrl: string) => void;
  addPartner: () => void;
  deletePartner: (id: string) => void;
  updateFaqItem: (id: string, field: keyof FaqItem, lang: Language, value: any) => void;
  addFaqItem: () => void;
  deleteFaqItem: (id: string) => void;
  updateBlogPost: (id: string, field: keyof BlogPost, lang: Language, value: any) => void;
  updateBlogPostImage: (id: string, url: string) => void;
  addBlogPost: () => void;
  deleteBlogPost: (id: string) => void;
  addCertificate: () => void;
  deleteCertificate: (id: string) => void;
  updateCertificate: (id: string, name: Record<Language, string>, imageUrl: string, scale?: number, position?: string) => void;
  addBadge: () => void;
  deleteBadge: (id: string) => void;
  updateBadge: (id: string, label: Record<Language, string>, imageUrl: string) => void;
  importViaAI: (url: string) => Promise<void>;
  autoTranslateProduct: (id: string, sourceLang: Language) => Promise<void>;
  importBlogViaAI: (url: string) => Promise<void>;
  importBlogFromText: (text: string) => Promise<void>;
  changeApiUrl: (url: string) => void;
}

const EditorContext = createContext<EditorContextType | undefined>(undefined);

export const EditorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error' | 'translating'>('idle');
  const [dataDescription, setDataDescription] = useState('Local Storage');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // 初始化使用常量中的 5 语种完整数据
  const [translations, setTranslations] = useState<Translations>(TRANSLATIONS);
  const [products, setProducts] = useState<Product[]>(PRODUCTS);
  const [cases, setCases] = useState<CaseStudy[]>(CASES);
  const [resources, setResources] = useState<Resource[]>(RESOURCES);
  const [testimonials, setTestimonials] = useState<Testimonial[]>(TESTIMONIALS);
  const [partners, setPartners] = useState<Partner[]>(PARTNERS);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>(BLOG_POSTS);
  const [faqItems, setFaqItems] = useState<FaqItem[]>(FAQ_ITEMS);
  const [certificates, setCertificates] = useState<Certificate[]>(CERTIFICATES);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [seoSettings, setSeoSettings] = useState<SEOSettings>({
    title: { [Language.EN]: '', [Language.ZH]: '', [Language.AR]: '', [Language.FR]: '', [Language.RU]: '' },
    description: { [Language.EN]: '', [Language.ZH]: '', [Language.AR]: '', [Language.FR]: '', [Language.RU]: '' }
  });
  const [hiddenNavItems, setHiddenNavItems] = useState<string[]>([]);
  const [currentApiUrl, setCurrentApiUrl] = useState(getBaseUrl());

  const applySiteData = useCallback((data: SiteData) => {
    // 关键修复：将云端接收到的 translations 与本地 TRANSLATIONS 常量深度合并
    // 确保即便云端缺失 ar/fr/ru，本地默认翻译也会补位
    if (data.translations) {
      setTranslations(deepMerge(TRANSLATIONS, data.translations));
    }
    
    // 对于产品和其他列表，如果云端有数据则覆盖
    if (data.products && data.products.length > 0) setProducts(data.products);
    if (data.cases) setCases(data.cases);
    if (data.resources) setResources(data.resources);
    if (data.testimonials) setTestimonials(data.testimonials);
    if (data.partners) setPartners(data.partners);
    if (data.seoSettings) setSeoSettings(data.seoSettings);
    if (data.hiddenNavItems) setHiddenNavItems(data.hiddenNavItems);
    if (data.blogPosts) setBlogPosts(data.blogPosts);
    if (data.faqItems) setFaqItems(data.faqItems);
    if (data.certificates) setCertificates(data.certificates);
    if (data.badges) setBadges(data.badges);
  }, []);

  const refreshData = useCallback(async () => {
    setDataDescription('Connecting...');
    const { data: remoteData, isOnline, error } = await fetchRemoteData();
    
    if (remoteData) {
      applySiteData(remoteData);
      localStorage.setItem(CACHE_KEY, JSON.stringify(remoteData));
      setDataDescription(`${getStorageType()} (Online: Synced)`);
    } else {
      setDataDescription(isOnline ? `${getStorageType()} (Online: Ready)` : `${getStorageType()} (Offline)`);
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        try { applySiteData(JSON.parse(cached)); } catch(e) {
          // Ignore JSON parse errors
        }
      }
    }
  }, [applySiteData]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const toggleEditMode = () => setIsEditMode(!isEditMode);

  const saveChanges = async () => {
    setSaveStatus('saving');
    const siteData: SiteData = {
      translations,
      products,
      cases,
      resources,
      testimonials,
      partners,
      seoSettings,
      hiddenNavItems,
      blogPosts,
      faqItems,
      certificates,
      badges,
      lastUpdated: new Date().toISOString()
    };

    const success = await saveRemoteData(siteData);
    if (success) {
      setSaveStatus('saved');
      setHasUnsavedChanges(false);
      localStorage.setItem(CACHE_KEY, JSON.stringify(siteData));
      setTimeout(() => setSaveStatus('idle'), 3000);
      setDataDescription(`${getStorageType()} (Online: Synced)`);
    } else {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const updateTranslation = (lang: Language, path: string, value: string) => {
    setTranslations(prev => {
      return setNestedValue(prev, `${lang}.${path}`, value);
    });
    setHasUnsavedChanges(true);
  };

  const updateProduct = (id: string, field: any, lang: Language, value: any) => {
    setProducts(prev => prev.map(p => {
      if (p.id !== id) return p;
      const newP = JSON.parse(JSON.stringify(p)); 
      if (field === 'features' || field === 'specs') {
         if (typeof value === 'string') {
            newP[field] = { ...newP[field], [lang]: value.split('\n').filter(Boolean) };
         } else {
            newP[field] = { ...newP[field], [lang]: value };
         }
      } else if (typeof newP[field] === 'object' && !Array.isArray(newP[field])) {
        if (!newP[field]) newP[field] = {};
        newP[field][lang] = value;
      } else if (field === 'name' || field === 'category' || field === 'description') {
        newP[field] = { [Language.EN]: newP[field], [lang]: value };
      } else {
        newP[field] = value;
      }
      return newP as Product;
    }));
    setHasUnsavedChanges(true);
  };

  const updateProductImage = (id: string, url: string) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, imageUrl: url } : p));
    setHasUnsavedChanges(true);
  };

  const deleteProduct = (id: string) => {
    setProducts(prev => prev.filter(p => p.id !== id));
    setHasUnsavedChanges(true);
  };

  const addProduct = () => {
    const newId = Math.random().toString(36).substr(2, 9);
    const newProduct: Product = {
      id: newId,
      code: 'NEW-001',
      name: { [Language.EN]: 'New Product', [Language.ZH]: '新产品', [Language.AR]: 'منتج جديد', [Language.FR]: 'Nouveau produit', [Language.RU]: 'Новый продукт' },
      category: { [Language.EN]: 'Flow', [Language.ZH]: '流量仪表', [Language.AR]: 'Flow', [Language.FR]: 'Flow', [Language.RU]: 'Flow' },
      description: { [Language.EN]: 'Description', [Language.ZH]: '描述', [Language.AR]: 'Description', [Language.FR]: 'Description', [Language.RU]: 'Description' },
      specs: { [Language.EN]: [], [Language.ZH]: [], [Language.AR]: [], [Language.FR]: [], [Language.RU]: [] },
      imageUrl: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=600',
      featured: false,
      features: { [Language.EN]: [], [Language.ZH]: [], [Language.AR]: [], [Language.FR]: [], [Language.RU]: [] }
    };
    setProducts(prev => [...prev, newProduct]);
    setHasUnsavedChanges(true);
  };

  const toggleProductFeatured = (id: string) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, featured: p.featured === false ? true : false } : p));
    setHasUnsavedChanges(true);
  };

  const reorderProducts = (newProducts: Product[]) => {
    setProducts(newProducts);
    setHasUnsavedChanges(true);
  };

  const updateCase = (id: string, field: any, lang: Language, value: any) => {
    setCases(prev => prev.map(c => {
      if (c.id !== id) return c;
      const newC = JSON.parse(JSON.stringify(c));
      if (typeof newC[field] === 'object' && !Array.isArray(newC[field])) {
        if (!newC[field]) newC[field] = {};
        newC[field][lang] = value;
      } else if (field === 'title' || field === 'location' || field === 'description') {
        newC[field] = { [Language.EN]: newC[field], [lang]: value };
      } else {
        newC[field] = value;
      }
      return newC as CaseStudy;
    }));
    setHasUnsavedChanges(true);
  };

  const updateCaseImage = (id: string, url: string) => {
    setCases(prev => prev.map(c => c.id === id ? { ...c, imageUrl: url } : c));
    setHasUnsavedChanges(true);
  };

  const addCase = () => {
    const newId = Math.random().toString(36).substr(2, 9);
    const newCase: CaseStudy = {
      id: newId,
      title: { [Language.EN]: 'New Solution', [Language.ZH]: '新方案', [Language.AR]: 'New Solution', [Language.FR]: 'New Solution', [Language.RU]: 'New Solution' },
      location: { [Language.EN]: 'Location', [Language.ZH]: '地点', [Language.AR]: 'Location', [Language.FR]: 'Location', [Language.RU]: 'Location' },
      description: { [Language.EN]: 'Description', [Language.ZH]: '描述', [Language.AR]: 'Description', [Language.FR]: 'Description', [Language.RU]: 'Description' },
      industry: 'water',
      imageUrl: 'https://images.unsplash.com/photo-1581093588402-4a7360e9a6b5?auto=format&fit=crop&q=80&w=600'
    };
    setCases(prev => [...prev, newCase]);
    setHasUnsavedChanges(true);
  };

  const deleteCase = (id: string) => {
    setCases(prev => prev.filter(c => c.id !== id));
    setHasUnsavedChanges(true);
  };

  const updateResource = (id: string, field: any, lang: Language, value: any) => {
    setResources(prev => prev.map(r => {
      if (r.id !== id) return r;
      const newR = JSON.parse(JSON.stringify(r));
      if (field === 'title') {
        if (!newR.title || typeof newR.title !== 'object') newR.title = { [Language.EN]: newR.title || '' };
        newR.title[lang] = value;
      } else {
        newR[field] = value;
      }
      return newR as Resource;
    }));
    setHasUnsavedChanges(true);
  };

  const updateResourceFile = (id: string, url: string, size: string) => {
    setResources(prev => prev.map(r => r.id === id ? { ...r, url, size, updateDate: new Date().toISOString().split('T')[0] } : r));
    setHasUnsavedChanges(true);
  };

  const addResource = () => {
    const newId = Math.random().toString(36).substr(2, 9);
    const newRes: Resource = {
      id: newId,
      title: { [Language.EN]: 'New Resource', [Language.ZH]: '新资源', [Language.AR]: 'New Resource', [Language.FR]: 'New Resource', [Language.RU]: 'New Resource' },
      type: 'pdf',
      size: '0 MB',
      url: '#',
      updateDate: new Date().toISOString().split('T')[0]
    };
    setResources(prev => [...prev, newRes]);
    setHasUnsavedChanges(true);
  };

  const deleteResource = (id: string) => {
    setResources(prev => prev.filter(r => r.id !== id));
    setHasUnsavedChanges(true);
  };

  const updateTestimonial = (id: string, field: any, lang: Language, value: any) => {
    setTestimonials(prev => prev.map(t => {
      if (t.id !== id) return t;
      const newT = JSON.parse(JSON.stringify(t));
      if (field === 'text') {
        if (!newT.text || typeof newT.text !== 'object') newT.text = { [Language.EN]: newT.text || '' };
        newT.text[lang] = value;
      } else {
        newT[field] = value;
      }
      return newT as Testimonial;
    }));
    setHasUnsavedChanges(true);
  };

  const addTestimonial = () => {
    const newId = Math.random().toString(36).substr(2, 9);
    const newT: Testimonial = {
      id: newId,
      name: 'Client Name',
      company: 'Company',
      country: 'Country',
      text: { [Language.EN]: 'Review', [Language.ZH]: '评价', [Language.AR]: 'Review', [Language.FR]: 'Review', [Language.RU]: 'Review' },
      rating: 5
    };
    setTestimonials(prev => [...prev, newT]);
    setHasUnsavedChanges(true);
  };

  const deleteTestimonial = (id: string) => {
    setTestimonials(prev => prev.filter(t => t.id !== id));
    setHasUnsavedChanges(true);
  };

  const reorderTestimonials = (newTestimonials: Testimonial[]) => {
    setTestimonials(newTestimonials);
    setHasUnsavedChanges(true);
  };

  const updatePartner = (id: string, name: string, logoUrl: string) => {
    setPartners(prev => prev.map(p => p.id === id ? { ...p, name, logoUrl } : p));
    setHasUnsavedChanges(true);
  };

  const addPartner = () => {
    const newId = Math.random().toString(36).substr(2, 9);
    setPartners(prev => [...prev, { id: newId, name: 'New Partner', logoUrl: '' }]);
    setHasUnsavedChanges(true);
  };

  const deletePartner = (id: string) => {
    setPartners(prev => prev.filter(p => p.id !== id));
    setHasUnsavedChanges(true);
  };

  const updateFaqItem = (id: string, field: any, lang: Language, value: any) => {
    setFaqItems(prev => prev.map(f => {
      if (f.id !== id) return f;
      const newF = JSON.parse(JSON.stringify(f));
      if (!newF[field] || typeof newF[field] !== 'object') newF[field] = { [Language.EN]: newF[field] || '' };
      newF[field][lang] = value;
      return newF as FaqItem;
    }));
    setHasUnsavedChanges(true);
  };

  const addFaqItem = () => {
    const newId = Math.random().toString(36).substr(2, 9);
    setFaqItems(prev => [...prev, {
      id: newId,
      question: { [Language.EN]: 'Question', [Language.ZH]: '问题', [Language.AR]: 'Question', [Language.FR]: 'Question', [Language.RU]: 'Question' },
      answer: { [Language.EN]: 'Answer', [Language.ZH]: '回答', [Language.AR]: 'Answer', [Language.FR]: 'Answer', [Language.RU]: 'Answer' }
    }]);
    setHasUnsavedChanges(true);
  };

  const deleteFaqItem = (id: string) => {
    setFaqItems(prev => prev.filter(f => f.id !== id));
    setHasUnsavedChanges(true);
  };

  const updateBlogPost = (id: string, field: any, lang: Language, value: any) => {
    setBlogPosts(prev => prev.map(p => {
      if (p.id !== id) return p;
      const newP = JSON.parse(JSON.stringify(p));
      if (typeof newP[field] === 'object' && !Array.isArray(newP[field])) {
        if (!newP[field]) newP[field] = {};
        newP[field][lang] = value;
      } else if (field === 'title' || field === 'excerpt' || field === 'content') {
        newP[field] = { [Language.EN]: newP[field] || '', [lang]: value };
      } else {
        newP[field] = value;
      }
      return newP as BlogPost;
    }));
    setHasUnsavedChanges(true);
  };

  const updateBlogPostImage = (id: string, url: string) => {
    setBlogPosts(prev => prev.map(p => p.id === id ? { ...p, imageUrl: url } : p));
    setHasUnsavedChanges(true);
  };

  const addBlogPost = () => {
    const newId = Math.random().toString(36).substr(2, 9);
    const newPost: BlogPost = {
      id: newId,
      title: { [Language.EN]: 'Title', [Language.ZH]: '标题', [Language.AR]: 'Title', [Language.FR]: 'Title', [Language.RU]: 'Title' },
      excerpt: { [Language.EN]: 'Excerpt', [Language.ZH]: '摘要', [Language.AR]: 'Excerpt', [Language.FR]: 'Excerpt', [Language.RU]: 'Excerpt' },
      content: { [Language.EN]: 'Content', [Language.ZH]: '内容', [Language.AR]: 'Content', [Language.FR]: 'Content', [Language.RU]: 'Content' },
      date: new Date().toISOString().split('T')[0],
      imageUrl: 'https://images.unsplash.com/photo-1532601224476-15c79f2f7a51?auto=format&fit=crop&q=80&w=600'
    };
    setBlogPosts(prev => [...prev, newPost]);
    setHasUnsavedChanges(true);
  };

  const deleteBlogPost = (id: string) => {
    setBlogPosts(prev => prev.filter(p => p.id !== id));
    setHasUnsavedChanges(true);
  };

  const addCertificate = () => {
    const newId = Math.random().toString(36).substr(2, 9);
    setCertificates(prev => [...prev, {
      id: newId,
      name: { [Language.EN]: 'New Certificate', [Language.ZH]: '新证书', [Language.AR]: 'New Certificate', [Language.FR]: 'New Certificate', [Language.RU]: 'New Certificate' },
      imageUrl: ''
    }]);
    setHasUnsavedChanges(true);
  };

  const deleteCertificate = (id: string) => {
    setCertificates(prev => prev.filter(c => c.id !== id));
    setHasUnsavedChanges(true);
  };

  const updateCertificate = (id: string, name: Record<Language, string>, imageUrl: string, scale?: number, position?: string) => {
    setCertificates(prev => prev.map(c => c.id === id ? { ...c, name, imageUrl, imageScale: scale, imagePosition: position } : c));
    setHasUnsavedChanges(true);
  };

  const addBadge = () => {
    const newId = Math.random().toString(36).substr(2, 9);
    setBadges(prev => [...prev, {
      id: newId,
      label: { [Language.EN]: 'New Badge', [Language.ZH]: '新徽章', [Language.AR]: 'New Badge', [Language.FR]: 'New Badge', [Language.RU]: 'New Badge' },
      imageUrl: ''
    }]);
    setHasUnsavedChanges(true);
  };

  const deleteBadge = (id: string) => {
    setBadges(prev => prev.filter(b => b.id !== id));
    setHasUnsavedChanges(true);
  };

  const updateBadge = (id: string, label: Record<Language, string>, imageUrl: string) => {
    setBadges(prev => prev.map(b => b.id === id ? { ...b, label, imageUrl } : b));
    setHasUnsavedChanges(true);
  };

  const importViaAI = async (url: string) => {
    setSaveStatus('translating');
    try {
      const data = await importProductFromUrl(url);
      if (data) {
        const newId = Math.random().toString(36).substr(2, 9);
        const product: Product = {
          id: newId,
          code: data.code || 'IMPORT-001',
          name: { [Language.EN]: data.name, [Language.ZH]: data.name, [Language.AR]: data.name, [Language.FR]: data.name, [Language.RU]: data.name },
          category: { [Language.EN]: data.category, [Language.ZH]: data.category, [Language.AR]: data.category, [Language.FR]: data.category, [Language.RU]: data.category },
          description: { [Language.EN]: data.description, [Language.ZH]: data.description, [Language.AR]: data.description, [Language.FR]: data.description, [Language.RU]: data.description },
          specs: { [Language.EN]: data.specs, [Language.ZH]: data.specs, [Language.AR]: data.specs, [Language.FR]: data.specs, [Language.RU]: data.specs },
          imageUrl: data.imageUrl,
          featured: false
        };
        setProducts(prev => [...prev, product]);
        setHasUnsavedChanges(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSaveStatus('idle');
    }
  };

  const autoTranslateProduct = async (id: string, sourceLang: Language) => {
    const product = products.find(p => p.id === id);
    if (!product) return;

    setSaveStatus('translating');
    try {
      const targetLangs = Object.values(Language).filter(l => l !== sourceLang);
      const nameTransl = await generateTranslations(product.name[sourceLang], targetLangs);
      const descTransl = await generateTranslations(product.description[sourceLang], targetLangs);
      
      setProducts(prev => prev.map(p => {
        if (p.id !== id) return p;
        return {
          ...p,
          name: { ...p.name, ...nameTransl },
          description: { ...p.description, ...descTransl }
        };
      }));
      setHasUnsavedChanges(true);
    } catch (e) {
      console.error(e);
    } finally {
      setSaveStatus('idle');
    }
  };

  const importBlogViaAI = async (url: string) => {
    setSaveStatus('translating');
    try {
      const data = await importArticleFromUrl(url);
      if (data) {
        const newId = Math.random().toString(36).substr(2, 9);
        const post: BlogPost = {
          id: newId,
          title: { [Language.EN]: data.title, [Language.ZH]: data.title, [Language.AR]: data.title, [Language.FR]: data.title, [Language.RU]: data.title },
          excerpt: { [Language.EN]: data.excerpt, [Language.ZH]: data.excerpt, [Language.AR]: data.excerpt, [Language.FR]: data.excerpt, [Language.RU]: data.excerpt },
          content: { [Language.EN]: data.content, [Language.ZH]: data.content, [Language.AR]: data.content, [Language.FR]: data.content, [Language.RU]: data.content },
          date: data.date || new Date().toISOString().split('T')[0],
          imageUrl: data.imageUrl || 'https://images.unsplash.com/photo-1532601224476-15c79f2f7a51?auto=format&fit=crop&q=80&w=600'
        };
        setBlogPosts(prev => [...prev, post]);
        setHasUnsavedChanges(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSaveStatus('idle');
    }
  };

  const importBlogFromText = async (text: string) => {
    setSaveStatus('translating');
    try {
      const data = await parseArticleFromText(text);
      if (data) {
        const newId = Math.random().toString(36).substr(2, 9);
        const post: BlogPost = {
          id: newId,
          title: { [Language.EN]: data.title, [Language.ZH]: data.title, [Language.AR]: data.title, [Language.FR]: data.title, [Language.RU]: data.title },
          excerpt: { [Language.EN]: data.excerpt, [Language.ZH]: data.excerpt, [Language.AR]: data.excerpt, [Language.FR]: data.excerpt, [Language.RU]: data.excerpt },
          content: { [Language.EN]: data.content, [Language.ZH]: data.content, [Language.AR]: data.content, [Language.FR]: data.content, [Language.RU]: data.content },
          date: data.date || new Date().toISOString().split('T')[0],
          imageUrl: 'https://images.unsplash.com/photo-1532601224476-15c79f2f7a51?auto=format&fit=crop&q=80&w=600'
        };
        setBlogPosts(prev => [...prev, post]);
        setHasUnsavedChanges(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSaveStatus('idle');
    }
  };

  const changeApiUrl = (url: string) => {
    setManualApiUrl(url);
    setCurrentApiUrl(getBaseUrl());
  };

  return (
    <EditorContext.Provider value={{
      isEditMode, toggleEditMode, saveChanges, refreshData, hasUnsavedChanges, saveStatus, dataDescription,
      translations, products, cases, resources, testimonials, partners, blogPosts, faqItems, certificates, badges, seoSettings, hiddenNavItems, currentApiUrl,
      updateTranslation, updateProduct, updateProductImage, deleteProduct, addProduct, toggleProductFeatured, reorderProducts,
      updateCase, updateCaseImage, addCase, deleteCase, updateResource, updateResourceFile, addResource, deleteResource,
      updateTestimonial, addTestimonial, deleteTestimonial, reorderTestimonials, updatePartner, addPartner, deletePartner,
      updateFaqItem, addFaqItem, deleteFaqItem, updateBlogPost, updateBlogPostImage, addBlogPost, deleteBlogPost,
      addCertificate, deleteCertificate, updateCertificate, addBadge, deleteBadge, updateBadge,
      importViaAI, autoTranslateProduct, importBlogViaAI, importBlogFromText, changeApiUrl
    }}>
      {children}
    </EditorContext.Provider>
  );
};

export const useEditor = () => {
  const context = useContext(EditorContext);
  if (!context) throw new Error("useEditor must be used within EditorProvider");
  return context;
};
